import { Injectable } from '@jkhy/frontend-utils/di';
import { AchBatchImportsClient } from '@treasury/api/channel';
import {
    AchBatchImport,
    type AchFileUploadResult,
    type AchRecentFile,
    type AchUpload422Response,
} from './ach-batch-import.entity';
import { AchFileActivityFilter } from './ach-file-activity-filter.entity';
import { AchService } from './ach.service';

@Injectable()
export class AchBatchImportService {
    constructor(
        private readonly client: AchBatchImportsClient,
        private readonly achService: AchService
    ) {}

    public async uploadNachaFile(file: File): Promise<AchFileUploadResult> {
        try {
            const response = await this.client.achBatchImportsPost({ file });
            const entity = new AchBatchImport(response.data);

            if (entity.hasError) {
                throw this.toUploadError(entity.errorMessage);
            }

            return entity.toUploadResult(file.name);
        } catch (error: unknown) {
            throw this.normalizeError(error);
        }
    }

    public async getRecentFiles(): Promise<AchRecentFile[]> {
        const filter = new AchFileActivityFilter({}).createDefault();
        const files = await this.achService.getAchFileActivity(filter);
        return files.map(f => f.toRecentFile());
    }

    private toUploadError(message: string): AchUpload422Response {
        return {
            type: 'upload_error',
            title: 'Upload Failed',
            details: [message],
            data: { errors: [] },
        };
    }

    private normalizeError(error: unknown): AchUpload422Response {
        if (this.isUpload422Response(error)) {
            return error;
        }

        return this.toUploadError(this.extractErrorMessage(error));
    }

    private extractErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }

        if (typeof error === 'object' && error !== undefined) {
            const obj = error as Record<string, unknown>;
            if ('message' in obj && typeof obj.message === 'string') {
                return obj.message;
            }
        }

        return 'An unknown error occurred while uploading the file.';
    }

    private isUpload422Response(error: unknown): error is AchUpload422Response {
        if (typeof error !== 'object' || error === undefined) {
            return false;
        }
        const obj = error as Record<string, unknown>;
        return 'type' in obj && 'title' in obj && 'data' in obj;
    }
}
